import { Plus, Trash2 } from 'lucide-react'
import { PALETTE_PRESETS } from '../chartPalette'
import { ColorField } from './controls'

type PalettePickerValue = string | string[] | undefined

interface PalettePickerProps {
  value: PalettePickerValue
  onChange: (next: PalettePickerValue) => void
}

const optionButtonClass = (active: boolean) =>
  `flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
    active
      ? 'border-2 border-[#6366F1] bg-[#F4F5FA] text-[#6366F1]'
      : 'border border-[#E2E4ED] bg-white text-[#1B2559] hover:bg-[#F4F5FA]'
  }`

/**
 * Sélecteur de palette pour un widget chart : options radio (thème / presets /
 * personnalisée) + éditeur de pastilles en mode personnalisé.
 */
export default function PalettePicker({ value, onChange }: PalettePickerProps) {
  const isCustom = Array.isArray(value)

  const enterCustom = () => {
    if (Array.isArray(value)) return
    onChange([...PALETTE_PRESETS[0].colors.slice(0, 4)])
  }

  const updateColor = (index: number, next: string | undefined) => {
    if (!Array.isArray(value)) return
    const nextArray = value.map((c, i) => (i === index ? next ?? c : c))
    onChange(nextArray)
  }

  const removeColor = (index: number) => {
    if (!Array.isArray(value)) return
    onChange(value.filter((_, i) => i !== index))
  }

  const addColor = () => {
    if (!Array.isArray(value)) return
    onChange([...value, '#6366F1'])
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={value === undefined}
          onClick={() => onChange(undefined)}
          className={optionButtonClass(value === undefined)}
        >
          Thème (défaut)
        </button>

        {PALETTE_PRESETS.map((preset) => {
          const active = value === preset.id
          return (
            <button
              key={preset.id}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(preset.id)}
              className={optionButtonClass(active)}
            >
              <span className="flex items-center gap-0.5">
                {preset.colors.slice(0, 4).map((color, i) => (
                  <span
                    key={i}
                    className="h-3 w-3 rounded-full border border-white"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </span>
              {preset.label}
            </button>
          )
        })}

        <button
          type="button"
          aria-pressed={isCustom}
          onClick={enterCustom}
          className={optionButtonClass(isCustom)}
        >
          Personnalisée
        </button>
      </div>

      {isCustom && (
        <div className="flex flex-col gap-2 rounded-lg border border-[#E2E4ED] bg-[#F4F5FA] p-3">
          {value.map((color, index) => (
            <div key={index} className="flex items-end gap-2">
              <div className="flex-1">
                <ColorField
                  label={`Couleur ${index + 1}`}
                  value={color}
                  onChange={(next) => updateColor(index, next)}
                />
              </div>
              <button
                type="button"
                aria-label={`Supprimer la couleur ${index + 1}`}
                onClick={() => removeColor(index)}
                className="mb-0.5 flex h-8 w-8 items-center justify-center rounded-md border border-[#E2E4ED] text-[#8B8FA7] hover:bg-white"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addColor}
            className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-[#6366F1] px-3 py-2 text-xs font-medium text-[#6366F1] hover:bg-white"
          >
            <Plus size={14} />
            Ajouter une couleur
          </button>
        </div>
      )}
    </div>
  )
}
